import { extractKeys } from '@fortem-monorepo/js-utilities'
import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { DataType, LogicType, MatcherType } from '@fortem-monorepo/react-spa'

import { CustomJobFormType } from '@/pages/monitoring/custom-jobs/components/CustomJobFormSchema'

// -------------------------------------------------

// 1. Turn form values into a command
// 2. Turn a command into form values

// -------------------------------------------------

export const keys = ['taskname', 'recipients', 'sender', 'subject', 'st_dt', 'ed_dt'] as const

/**
 * Generate a command string from form values.
 */
export const getCommandFromFormValues = ({
  taskname,
  recipients,
  sender,
  subject,
  st_dt,
  ed_dt,
}: CustomJobFormType) => {
  let commandString = `--taskname "${taskname}" --st_dt "${toISO8601(st_dt)}"`

  if (ed_dt) {
    commandString += ` --ed_dt "${toISO8601(ed_dt)}"`
  }

  const logicValues = { recipients, sender, subject }

  Object.entries(logicValues).forEach(([key, lines]) => {
    lines.forEach((line) => {
      const logic = line.logic ? `${line.logic}:` : ''
      const matcher = line.matcher ? `${line.matcher}:` : ''
      const value = line.value

      commandString += ` --${key} "${logic}${matcher}${value}"`
    })
  })

  commandString = commandString.trim()
  return commandString
}

/**
 * Parse a command string and get form values from it.
 */
export const getFormValuesFromCommand = (command: string): CustomJobFormType => {
  const keyValues = extractKeys(command, keys)

  const {
    taskname: [taskname],
    recipients,
    sender,
    subject,
    st_dt: [st_dt],
    ed_dt: [ed_dt],
  } = keyValues

  const logicFields = { recipients, sender, subject } as const

  const fromDate = new Date(st_dt)
  const toDate = ed_dt ? new Date(ed_dt) : undefined

  const formData: CustomJobFormType = {
    taskname,
    recipients: [],
    sender: [],
    subject: [],
    st_dt: fromDate,
    ed_dt: toDate,
  }

  Object.entries(logicFields).forEach(([key, values]) => {
    const inputs: Array<DataType> = []

    values.forEach((_string) => {
      // 1. Get logic, matcher & value
      const matches = Array.from([
        ..._string.matchAll(/^(?:(or|and):)*(?:(contains|matches|startswith|endswith):)*(.*)+$/gm),
      ])[0]

      if (!matches?.length) {
        return
      }

      const [, logic, matcher, value] = matches

      inputs.push({
        logic: logic as LogicType,
        matcher: matcher as MatcherType,
        value,
      })
    })

    // Because we map through form fields, the first item inside LogicHandlers
    // Need to have their logic ignored as the title string is displayed isntead of
    // the select dropdown.
    // ...
    // Therefore, sort the logic array for a given field, ensuring the first element is
    // the one without logic.
    inputs.sort((a, b) => (a.logic === b.logic ? 0 : b.logic ? -1 : 1))
    formData[key as keyof typeof logicFields] = inputs
  })

  return formData
}
